import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AvatarUploaderComponent } from '../../../../helpers/components/avatar-uploader/avatar-uploader';
import { ImagesLazyLoadDirective } from '../../../../helpers/directives/images-lazy-load';
import { ModalController } from '@ionic/angular';

import { Config } from '@cbcore/services/config/config.service';
import { ImageClient } from '@cbcore/client/image';
import { ImageService } from '@cbcore/services/utils/image/image.service';
import { ManagerClient } from '@cbcore/client/manager';
import * as _ from 'lodash-es';
import { take } from 'rxjs/operators';
import { AppState, callMicrositeSessions } from '@cbcore/NGRX/core.state';
import { Store } from '@ngrx/store';
import { getManager } from '@cbcore/NGRX/manager/manager.actions';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { PipesModule } from '@cbcore/pipes/pipes.module';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'syndicate-profile-create-component',
  templateUrl: './syndicate-profile-create.html',
  styleUrls: ['./syndicate-profile-create.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    PipesModule,
    TranslocoModule,
    CommonModule,
    AvatarUploaderComponent,
    ImagesLazyLoadDirective,
  ],
})
export class SyndicateProfileCreateComponent implements OnInit, AfterViewInit {
  public defaultManager: any = {};
  public imageList!: any;
  private _showSelector!: boolean;

  // Opening or closing the selector (re)creates the uploader, so any rejection it reported no longer applies
  get showSelector(): boolean {
    return this._showSelector;
  }
  set showSelector(value: boolean) {
    this._showSelector = value;
    this.avatarError = false;
  }
  public isProfileImageUpdated?: boolean; // Only assigned when selectImage()/setCroppedImage() are called
  public error!: string | null | undefined;
  public haveManagerTwitter!: boolean; // Assigned unconditionally in ngOnInit — pre-existing assumption, not independently verified
  public viewList: boolean = false;
  public avatarError: boolean = false; // uploader rejected the picked file (type/size)
  public avatarCropped: File | null = null;
  public isCropping: boolean | null = null;
  public android: boolean | null = null;

  public imagesUrl!: string; // Assigned unconditionally in ngOnInit — pre-existing assumption, not independently verified
  public fallbackMangerImage!: string; // Assigned unconditionally in ngOnInit — pre-existing assumption, not independently verified

  public step: number = 1;

  @ViewChild('username') usernameInput!: ElementRef; // Resolved by Angular after view init, before any consumer read

  @Output() closingModal: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    public _managerClient: ManagerClient,
    public _imageClient: ImageClient,
    public _config: Config,
    public _imageService: ImageService,
    public store: Store<AppState>,
    private modalCtrl: ModalController,
  ) {}

  ngOnInit(): void {
    this.haveManagerTwitter = this._config.CONFIG.haveManagerTwitter;
    this.imagesUrl = this._config.CONFIG.imagesUrl;
    this.fallbackMangerImage =
      this._config.CONFIG.fallbackSyndicateProfileImage;
    this._imageClient
      .getConfig()
      .pipe(take(1))
      .subscribe((data) => this.updateImages(data));
  }

  ngAfterViewInit(): void {
    this.usernameInput.nativeElement.focus();
  }

  updateImages(data: any): void {
    this.imageList = this._imageService.createImageList(
      this._config.CONFIG.imagesUrl,
      data,
    );
    this.defaultManager.picture_url =
      typeof this.defaultManager.picture_url === 'undefined'
        ? _.sampleSize(this.imageList, 1)
        : this.defaultManager.picture_url;
  }

  cancelCreate(): void {
    this.modalCtrl.dismiss();
  }

  // Actions stay disabled while the chosen picture is known to be rejected
  get imageBlocked(): boolean {
    return this.avatarError || this.error === 'FILE_TOO_BIG';
  }

  setViewList(viewList: boolean): void {
    this.viewList = viewList;
    this.clearImageError();
  }

  private clearImageError(): void {
    this.avatarError = false;
    if (this.error === 'FILE_TOO_BIG') {
      this.error = undefined;
    }
  }

  selectImage(image: any, toggle: boolean): void {
    this.clearImageError();
    this.defaultManager.picture_url = image;
    this.avatarCropped = null;
    if (toggle) {
      this.showSelector = !this.showSelector;
    }
    this.isProfileImageUpdated = true;
  }

  setFallbackImage(): void {
    this.defaultManager.picture_url = this.imagesUrl + this.fallbackMangerImage;
  }

  createManager(): void {
    if (this.imageBlocked) return;
    this.error = undefined;
    let regex: any = new RegExp('^[\sA-z0-9_ -]{1,32}$');

    if (typeof this.defaultManager.username === 'undefined') {
      this.error = 'PROFILE_EMPTY_USERNAME';
      return;
    }

    if (!regex.test(this.defaultManager.username)) {
      this.error = 'PROFILE_USERNAME_INVALID';
      return;
    }

    this.defaultManager.username = this.removeEmojis(
      this.defaultManager.username,
    );

    // Payload and request
    let payload: any = {
      manager: {
        username: String(this.defaultManager.username).trim(),
        twitter_username: this.defaultManager.twitter_username,
        // tslint:disable-next-line: max-line-length
        picture_url: this.avatarCropped
          ? undefined
          : this.isProfileImageUpdated
            ? this.defaultManager.picture_url
            : _.shuffle(this.imageList)[0],
        picture: this.avatarCropped || undefined,
      },
    };

    this._managerClient.create(payload).subscribe(
      (result) => {
        this.defaultManager = result;
        this.step++;
      },
      (error) => {
        this.error =
          error.errors?.[0]?.code === 'FILE_TOO_BIG'
            ? 'FILE_TOO_BIG'
            : error.code;
      },
    );
  }

  updateManager(): void {
    if (this.imageBlocked) return;
    this.error = undefined;

    let payload: any = {
      id: this.defaultManager.id,
      manager: {
        picture_url: this.defaultManager.picture_url,
        twitter_username: this.defaultManager.twitter_username,
        picture: this.avatarCropped || undefined,
      },
    };

    this._managerClient.update(payload).subscribe(
      (result) => {
        this.closingModal.emit(true);
        this.store.dispatch(callMicrositeSessions());
        this.store.dispatch(getManager({ id: this.defaultManager.id }));
        this.cancelCreate();
      },
      (error) => {
        this.error =
          error.errors?.[0]?.code === 'FILE_TOO_BIG'
            ? 'FILE_TOO_BIG'
            : error.code;
      },
    );
  }

  removeEmojis(string: string): string {
    // tslint:disable-next-line: max-line-length
    const regex: RegExp =
      /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
    return string.replace(regex, '');
  }

  setCroppedImage(event: any): void {
    this.clearImageError();
    this.defaultManager.picture_url = event;
    this.avatarCropped = event;
    this.isProfileImageUpdated = true;
    this.showSelector = false;
  }

  manageCropping(e: any): void {
    this.isCropping = e;
  }
}
